import React, { MouseEventHandler, PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Toast as ToastStyles } from '@airbus/elements/elements'
import { Close as CloseIcon } from '@mui/icons-material'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useTimeout from '../../lib/hooks/useTimeout'
import { createUseStyles } from '../../lib/styling'
import IconButton from '../IconButton'
import Link from '../Link'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(ToastStyles, 'toast')

type Variants = 'info' | 'success' | 'warning' | 'error'

type Props = PropsWithChildren<{
  /** Show close icon button */
  allowClose?: boolean
  /** Delay in milliseconds to hide notification */
  autoHideDuration?: number
  /** Inline mode with larger size */
  inline?: boolean
  onClick?: MouseEventHandler<HTMLDivElement>
  onMouseDown?: MouseEventHandler<HTMLDivElement>
  /** Called when user closes notification, or after `autoHideDuration` */
  onClose?: () => void
  /** Time of the notification */
  time?: string
  /** Title of the notification */
  title?: string
  /**
   * The variant of the chip.
   * @default info
   */
  variant?: Variants
}>

type DeprecatedProps = {
  role?: 'status' | 'alert'
  status?: Variants
}

export type ToastProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: ToastProps) => {
  const { status, variant, ...rest } = props
  return {
    variant: status || variant,
    ...rest,
  }
}

const Toast = (props: ToastProps) => {
  const { allowClose, autoHideDuration, children, className, inline, onClose, time, title, variant, ...rest } =
    handleDeprecatedProps(props)
  useDeprecatedProps('Toast', { role: null, status: 'variant' }, props)
  const classes = useStyles()

  const handleClose = () => onClose && onClose()
  useTimeout(handleClose, autoHideDuration)

  // auto set Link children props
  const childrenWithProps = React.useMemo(
    () =>
      React.Children.map(children, (child) => {
        if (!React.isValidElement(child) || child.type !== Link) return child
        return React.cloneElement<any>(child, {
          ...child.props,
          inline: true,
          size: 'small',
          variant: 'underlined',
        })
      }),
    [children],
  )

  return (
    <div
      className={clsx(classes.root, className, {
        [classes._error]: variant === 'error',
        [classes._success]: variant === 'success',
        [classes._warning]: variant === 'warning',
        [classes._info]: variant === 'info',
        [classes._inline]: inline !== undefined,
        [classes._withRemove]: allowClose,
      })}
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      {...rest}
    >
      {title && <div className={classes.heading}>{title}</div>}
      {time && <div className={classes.time}>{time}</div>}
      {childrenWithProps && <div className={classes.body}>{childrenWithProps}</div>}
      {allowClose && (
        <IconButton
          aria-label='Remove'
          className={classes.removeBtn}
          size='small'
          variant='ghost'
          onClick={handleClose}
          onTouchEnd={(e: any) => {
            e.preventDefault()
            handleClose()
          }}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <CloseIcon />
        </IconButton>
      )}
    </div>
  )
}

export default Toast

// retrocompat : Deprecated component name
export const Notification = (props: ToastProps) => {
  useDeprecatedComponent('Notification', 'Please use Toast.')
  return <Toast {...props} />
}
